import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { usePermissions } from '../hooks/usePermissions'
import { useAuth } from '../contexts/AuthContext'
import { normalizeRole } from '../lib/permissions'

interface PermissionGateProps {
  permission: string | string[]
  children: React.ReactNode
  fallback?: React.ReactNode
  redirectTo?: string
}

export function PermissionGate({
  permission,
  children,
  fallback,
  redirectTo = '/access-denied',
}: PermissionGateProps) {
  const { loading, permissionLoading } = useAuth()
  const { canAny } = usePermissions()

  // Wait for session AND permissions — otherwise empty perms redirect everyone.
  if (loading || permissionLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-gray-500">Loading...</div>
      </div>
    )
  }

  const required = Array.isArray(permission) ? permission : [permission]
  const allowed = canAny(required)

  if (!allowed) {
    if (fallback) return <>{fallback}</>
    return <Navigate to={redirectTo} replace />
  }

  return <>{children}</>
}

export function CustomerRedirect({ children }: { children: React.ReactNode }) {
  const { profile, loading } = useAuth()
  const location = useLocation()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    )
  }

  // Only redirect away — never redirect the /client-portal route itself, or the
  // portal page never gets a chance to render for the customer role.
  if (
    profile &&
    normalizeRole(profile.role) === 'customer' &&
    location.pathname !== '/client-portal'
  ) {
    return <Navigate to="/client-portal" replace />
  }

  return <>{children}</>
}
